import {
  AnswerChangeSet,
  computeAnswerChanges,
  formAnswerToOutDto,
} from 'shared/domain/checklistAnswer/answerDiff';
import {
  ChecklistAnswerCreateModel,
  ChecklistAnswerEditModel,
  ChecklistAnswerDeleteModel,
} from 'shared/domain/checklistAnswer/types/model';
import { FormChecklistAnswer } from 'shared/domain/checklistAnswer/types/formAnswer';
import { AnswerType } from 'shared/domain/checklistAnswer/types/answerType';
import { ChecklistItemInDto } from 'shared/dtos/in/template';
import { FormChecklistItem } from 'views/template/wizard/form/types';
import { TemplateDefaultValues } from 'views/template/wizard/populator/types';
import { Translate } from 'intl/translate';

/**
 * Creates a promise for creating a single answer.
 */
function createAnswerPromise(
  templateId: string,
  itemId: string,
  answer: FormChecklistAnswer,
  projectId: string,
  translate: Translate,
  createFn: (model: ChecklistAnswerCreateModel) => Promise<unknown>
): Promise<unknown> {
  const model: ChecklistAnswerCreateModel = {
    projectId,
    inspectionTemplateId: templateId,
    checklistItemId: itemId,
    answer: formAnswerToOutDto(answer, translate),
  };
  return createFn(model);
}

/**
 * Creates a promise for updating a single answer.
 */
function updateAnswerPromise(
  templateId: string,
  itemId: string,
  answer: FormChecklistAnswer,
  projectId: string,
  translate: Translate,
  updateFn: (model: ChecklistAnswerEditModel) => Promise<unknown>
): Promise<unknown> {
  if (!answer._id) {
    return Promise.resolve();
  }
  const model: ChecklistAnswerEditModel = {
    _id: answer._id,
    projectId,
    inspectionTemplateId: templateId,
    checklistItemId: itemId,
    answer: formAnswerToOutDto(answer, translate),
  };
  return updateFn(model);
}

/**
 * Creates a promise for deleting a single answer.
 */
function deleteAnswerPromise(
  templateId: string,
  itemId: string,
  answerId: string,
  projectId: string,
  removeFn: (model: ChecklistAnswerDeleteModel) => Promise<unknown>
): Promise<unknown> {
  const model: ChecklistAnswerDeleteModel = {
    _id: answerId,
    projectId,
    inspectionTemplateId: templateId,
    checklistItemId: itemId,
  };
  return removeFn(model);
}

export interface AnswerSyncDependencies {
  translate: Translate;
  createChecklistAnswer: (model: ChecklistAnswerCreateModel) => Promise<unknown>;
  updateChecklistAnswer: (model: ChecklistAnswerEditModel) => Promise<unknown>;
  removeChecklistAnswer: (model: ChecklistAnswerDeleteModel) => Promise<unknown>;
}

export type CreateAnswersForNewItemsDependencies = Pick<
  AnswerSyncDependencies,
  'createChecklistAnswer' | 'updateChecklistAnswer' | 'translate'
>;

/**
 * Processes a single change set and returns promises for create and update operations.
 */
function processChangeSet(
  templateId: string,
  projectId: string,
  changeSet: AnswerChangeSet,
  deps: AnswerSyncDependencies
): Promise<unknown>[] {
  const promises: Promise<unknown>[] = [];

  changeSet.created.forEach((answer) => {
    promises.push(
      createAnswerPromise(
        templateId,
        changeSet.itemId,
        answer,
        projectId,
        deps.translate,
        deps.createChecklistAnswer
      )
    );
  });

  changeSet.updated.forEach((answer) => {
    promises.push(
      updateAnswerPromise(
        templateId,
        changeSet.itemId,
        answer,
        projectId,
        deps.translate,
        deps.updateChecklistAnswer
      )
    );
  });

  return promises;
}

/**
 * Synchronizes answer changes to the backend.
 * This handles create, update, and delete operations for answers
 * across multiple checklist items.
 */
export async function syncAnswerChanges(
  templateId: string,
  projectId: string,
  previousValues: Readonly<TemplateDefaultValues>,
  checklist: readonly FormChecklistItem[],
  deps: AnswerSyncDependencies
): Promise<void> {
  const answerChanges = computeAnswerChanges(
    previousValues.checklist,
    checklist
  );

  if (!answerChanges.hasChanges) {
    return;
  }

  const allPromises: Promise<unknown>[] = [];

  answerChanges.changeSets.forEach((changeSet) => {
    const setPromises = processChangeSet(
      templateId,
      projectId,
      changeSet,
      deps
    );
    allPromises.push(...setPromises);
  });

  await Promise.all(allPromises);

  for (const changeSet of answerChanges.changeSets) {
    for (const answerId of changeSet.deleted) {
      await deleteAnswerPromise(
        templateId,
        changeSet.itemId,
        answerId,
        projectId,
        deps.removeChecklistAnswer
      );
    }
  }
}

/**
 * Creates answers for newly created checklist items.
 * Compliance answers are updated on the default answer seeded by the backend.
 */
export async function createAnswersForNewItems(
  templateId: string,
  projectId: string,
  checklist: readonly FormChecklistItem[],
  createdItemsByUniqueKeyId: ReadonlyMap<string, ChecklistItemInDto>,
  deps: CreateAnswersForNewItemsDependencies
): Promise<void> {
  const promises: Promise<unknown>[] = [];

  checklist.forEach((item) => {
    const createdItem = createdItemsByUniqueKeyId.get(item.uniqueKeyId);
    if (!createdItem?._id) return;

    (item.answers || []).forEach((answer) => {
      const seededComplianceId =
        answer.type === AnswerType.Compliance
          ? createdItem.answers?.find(
              (seeded) => seeded.type === AnswerType.Compliance
            )?._id
          : undefined;

      if (answer.type === AnswerType.Compliance) {
        if (seededComplianceId) {
          promises.push(
            updateAnswerPromise(
              templateId,
              createdItem._id,
              { ...answer, _id: seededComplianceId },
              projectId,
              deps.translate,
              deps.updateChecklistAnswer
            )
          );
        } else {
          promises.push(
            createAnswerPromise(
              templateId,
              createdItem._id,
              answer,
              projectId,
              deps.translate,
              deps.createChecklistAnswer
            )
          );
        }
        return;
      }

      promises.push(
        createAnswerPromise(
          templateId,
          createdItem._id,
          answer,
          projectId,
          deps.translate,
          deps.createChecklistAnswer
        )
      );
    });
  });

  if (promises.length > 0) {
    await Promise.all(promises);
  }
}
